import styled, { DefaultTheme } from 'styled-components';
import { Modal, Button } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const StyledModal = styled(Modal)`
  .ant-modal-header {
    background-color: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    font-weight: 600;
  }

  .ant-modal-body {
    background-color: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    padding: 24px;
  }

  .ant-modal-footer {
    background-color: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }
`;

export const AlertBox = styled.div<{ type: 'warning' | 'info' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid
    ${(props) =>
      props.type === 'warning'
        ? getThemeValue(props, '#d46b08', '#ffd666')
        : getThemeValue(props, '#1890ff', '#91d5ff')};
  background-color: ${(props) =>
    props.type === 'warning'
      ? getThemeValue(props, '#2b2410', '#fffbe6')
      : getThemeValue(props, '#111d2c', '#e6f7ff')};
`;

export const AlertTitle = styled.p<{ type?: 'warning' | 'info' }>`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) =>
    props.type === 'warning'
      ? getThemeValue(props, '#ffd666', '#ad6800')
      : getThemeValue(props, '#1890ff', '#0050b3')};
`;

export const AlertDescription = styled.p<{ type?: 'warning' | 'info' }>`
  margin: 4px 0 0 0;
  font-size: 12px;
  color: ${(props) =>
    props.type === 'warning'
      ? getThemeValue(props, '#ffd666', '#ad6800')
      : getThemeValue(props, '#1890ff', '#0050b3')};
`;

export const FormSection = styled.div`
  margin-bottom: 24px;
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};

  &.required::after {
    content: ' *';
    color: #ff4d4f;
  }
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 6px;
  background-color: ${(props) => getThemeValue(props, '#141414', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &::placeholder {
    color: ${(props) => getThemeValue(props, '#595959', '#8c8c8c')};
  }

  &[as='select'] {
    cursor: pointer;
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 14px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 6px;
  background-color: ${(props) => getThemeValue(props, '#141414', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &::placeholder {
    color: ${(props) => getThemeValue(props, '#595959', '#8c8c8c')};
  }
`;

export const EditorContainer = styled.div`
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 6px;
  background-color: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  overflow: hidden;
`;

export const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  background-color: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
`;

export const EditorModeButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const ModeButton = styled(Button)<{ active?: boolean }>`
  padding: 4px 12px;
  font-size: 12px;
  ${(props) => props.active && 'border-color: #1890ff; color: #1890ff;'}
`;

export const QuillEditorWrapper = styled.div`
  .ql-container {
    background-color: ${(props) => getThemeValue(props, '#141414', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-size: 14px;
  }

  .ql-editor {
    min-height: 400px;
    padding: 16px;
    background-color: ${(props) => getThemeValue(props, '#141414', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};

    &.ql-blank::before {
      color: ${(props) => getThemeValue(props, '#595959', '#bfbfbf')};
    }
  }

  .ql-toolbar {
    background-color: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};

    button {
      color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    }

    button:hover {
      color: #1890ff;
    }

    button.ql-active {
      color: #1890ff;
    }
  }

  .ql-stroke {
    stroke: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  }

  .ql-stroke.ql-active,
  .ql-stroke.ql-hover,
  .ql-picker-label {
    stroke: #1890ff;
  }

  .ql-fill,
  .ql-stroke.ql-fill {
    fill: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  }

  .ql-fill.ql-active,
  .ql-fill.ql-hover,
  .ql-stroke.ql-fill.ql-active,
  .ql-stroke.ql-fill.ql-hover {
    fill: #1890ff;
  }
`;

export const PreviewPanel = styled.div`
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 6px;
  min-height: 300px;
  background-color: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  overflow: auto;
`;

export const PreviewHeader = styled.div`
  padding: 12px 16px;
  background-color: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
`;

export const PreviewHeaderItem = styled.p`
  margin: 0 0 8px 0;
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  word-break: break-all;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const PreviewContent = styled.div`
  padding: 16px 24px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#333333')};
  line-height: 1.6;
  font-family: 'Arial', sans-serif;

  img {
    max-width: 100%;
    height: auto;
  }
`;

export const AttachmentDropZone = styled.div`
  border: 2px dashed ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 6px;
  padding: 32px 16px;
  text-align: center;
  background-color: ${(props) => getThemeValue(props, '#141414', '#fafafa')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => getThemeValue(props, '#262626', '#f5f5f5')};
    border-color: #1890ff;
  }
`;

export const AttachmentDropZoneText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
`;

export const AttachmentList = styled.div`
  margin-top: 16px;
`;

export const AttachmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: ${(props) => getThemeValue(props, '#262626', '#f5f5f5')};
  border-radius: 6px;
  margin-bottom: 8px;
`;

export const AttachmentItemName = styled.span`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
`;

export const SidebarPanel = styled.div`
  padding: 16px;
  background-color: ${(props) => getThemeValue(props, '#1f1f1f', '#fafafa')};
  border-radius: 6px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#f0f0f0')};
`;

export const SidebarTitle = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
`;

export const SidebarSubtitle = styled.p`
  margin: 0 0 8px 0;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#595959', '#8c8c8c')};
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const VariableButton = styled(Button)`
  font-size: 12px;
  cursor: pointer;
`;

export const FormFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
`;

export const FormFooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 2}, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ErrorText = styled.p`
  margin: 4px 0 0 0;
  font-size: 12px;
  color: #ff4d4f;
`;

export const HelperText = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#595959', '#8c8c8c')};
`;
